import { Autocomplete, Box, Button, Chip, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useAddApprover, useApprovers, useRemoveApprover } from "@/hooks/useAdjustments";
import { usePermissions } from "@/hooks/usePermissions";
import { useUsers } from "@/hooks/useUsers";
import type { User } from "@/interfaces/user";

/**
 * The checker pool — who may act on the approval queue.
 *
 * Deliberately separate from roles. A role says what a job function may reach; being trusted to
 * approve a colleague's credit movement is a named decision about a person, made and revoked one at
 * a time. Holding approve/CREDIT only opens this surface; being listed here is what lets someone
 * decide.
 *
 * Managing the list is Super Admin only (edit/CREDIT). Everyone who can approve may READ it, so a
 * checker can see who else could take a request they cannot act on themselves.
 */
export default function ApproversPanel() {
  const { can } = usePermissions();
  const canManage = can("edit", "CREDIT");

  const { data: approvers = [], isLoading } = useApprovers();
  const add = useAddApprover();
  const remove = useRemoveApprover();

  const [pick, setPick] = useState<User | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  // GFG staff only — the server refuses anyone else, so offering them would only produce an error.
  const { data: usersResp } = useUsers({ search: search || undefined, gfgOnly: true, limit: 20 });
  const inPool = new Set(approvers.filter((a) => a.isActive).map((a) => a.userId));
  const candidates = (usersResp?.data ?? []).filter((u) => !inPool.has(u.id));

  const active = approvers.filter((a) => a.isActive);
  const stoodDown = approvers.filter((a) => !a.isActive);

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <Box sx={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 14, color: "var(--color-graphite)" }}>
        Only people listed here can approve. A request can never be approved by whoever raised it,
        so at least two active approvers are needed for anything to clear.
      </Typography>

      {active.length < 2 && !isLoading ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-error, #b3261e)" }}>
          {active.length === 0
            ? "Nobody can approve anything — the pool is empty."
            : "Only one active approver: they cannot approve their own requests, so anything they raise will stall."}
        </Typography>
      ) : null}

      {canManage ? (
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={candidates}
            value={pick}
            getOptionLabel={(u) => `${u.name ?? "—"} (${u.email ?? ""})`}
            onInputChange={(_, v) => setSearch(v)}
            onChange={(_, v) => setPick(v)}
            renderInput={(p) => <TextField {...p} label="Add a GFG approver" size="small" />}
          />
          <Button
            variant="contained"
            disabled={!pick || add.isPending}
            onClick={() =>
              run(async () => {
                if (pick) await add.mutateAsync(pick.id);
                setPick(null);
                setSearch("");
              })
            }
          >
            Add
          </Button>
        </Box>
      ) : null}

      {error ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-error, #b3261e)" }}>{error}</Typography>
      ) : null}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {active.map((a) => (
          <Box
            key={a.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              border: "1px solid var(--color-card-border)",
              borderRadius: "var(--radius-card, 8px)",
              px: 2,
              py: 1.25,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{a.name ?? "—"}</Typography>
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                {a.email ?? "—"}
              </Typography>
            </Box>
            {canManage ? (
              <Button
                size="small"
                variant="outlined"
                disabled={remove.isPending}
                onClick={() => run(() => remove.mutateAsync(a.userId))}
              >
                Stand down
              </Button>
            ) : null}
          </Box>
        ))}
      </Box>

      {stoodDown.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", opacity: 0.6, mb: 1 }}
          >
            Stood down
          </Typography>
          {/* Kept, not deleted: their past decisions still have to resolve to a person. */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {stoodDown.map((a) => (
              <Chip
                key={a.id}
                size="small"
                variant="outlined"
                label={a.name ?? a.email ?? `#${a.userId}`}
                onClick={canManage ? () => run(() => add.mutateAsync(a.userId)) : undefined}
                title={canManage ? "Reinstate" : undefined}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
